import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Container,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useMediaQuery,
  useTheme,
  Menu,
  MenuItem,
} from '@mui/material';
import styled from 'styled-components';
import MenuIcon from '@mui/icons-material/Menu';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { motion } from 'framer-motion';

// Context
import { useI18n } from '../../context/I18nContext';

// Tipo para idiomas suportados
type SupportedLanguage = 'pt' | 'en' | 'es';

// Estilos
const StyledAppBar = styled(AppBar)`
  background-color: #ffffff !important;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  backdrop-filter: none;
  opacity: 1;
`;

const Logo = styled.img`
  height: 60px;
  margin-right: 16px;
  display: block;
`;

const MenuContainer = styled(Box)`
  display: flex;
  flex-grow: 1;
  justify-content: center;
  margin: 0 auto;
`;

const NavButton = styled(Button)`
  margin: 0 15px;
  color: var(--text-color);
  position: relative;
  text-transform: none;
  font-weight: 500;
  font-size: 1rem;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 0%;
    height: 3px;
    background-color: var(--primary-color);
    transition: var(--transition);
  }
  
  &:hover::after, &.active::after {
    width: 100%;
  }

  &:hover::after {
    margin-bottom: -3px;
    opacity: 0.5;
  }
  
  &.active {
    color: var(--primary-color);
  }
`;

const LanguageSelector = styled(Box)`
  display: flex;
  align-items: center;
  cursor: pointer;
  border-radius: 24px;
  padding: 6px 12px;
  transition: var(--transition);
  border: 1px solid rgba(0, 77, 64, 0.1);
  background-color: rgba(0, 77, 64, 0.03);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  
  &:hover {
    background-color: rgba(0, 77, 64, 0.08);
    border-color: rgba(0, 77, 64, 0.2);
  }
`;

const LanguageFlag = styled.img`
  width: 24px;
  height: 16px;
  border-radius: 2px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
  transition: var(--transition);
  
  &.active {
    box-shadow: 0 2px 4px rgba(0, 77, 64, 0.2);
  }
`;

const Header: React.FC = () => {
  const { language, setLanguage, t } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [mobileOpen, setMobileOpen] = useState(false);
  const [languageMenuAnchor, setLanguageMenuAnchor] = useState<null | HTMLElement>(null);
  
  // Prevenir scroll quando o drawer estiver aberto
  React.useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileOpen]);
  
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };
  
  const handleLanguageMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setLanguageMenuAnchor(event.currentTarget);
    // Não fechar o drawer quando abrir o menu de idiomas
    event.stopPropagation();
  };
  
  const handleLanguageMenuClose = () => {
    setLanguageMenuAnchor(null);
  };
  
  const changeLanguage = (lang: SupportedLanguage) => {
    setLanguage(lang);
    handleLanguageMenuClose();
    // Evitar que o menu lateral se feche ao mudar o idioma (no mobile)
    // Apenas propagar o evento se não estiver no menu lateral
  };
  
  const isActive = (path: string) => {
    if (path === '/' && location.pathname === '/') return true;
    if (path !== '/' && location.pathname.startsWith(path)) return true;
    return false;
  };
  
  const navItems = [
    { label: t('nav_home'), path: '/' },
    { label: t('nav_about'), path: '/about' },
    { label: t('nav_how_works'), path: '/how-works' },
    { label: t('nav_cities'), path: '/cidades' },
    { label: t('nav_map'), path: '/map' },
    { label: t('nav_contact'), path: '/contact' },
  ];
  
  const flagImages: Record<SupportedLanguage, string> = {
    pt: '/assets/images/flags/brazil.png',
    en: '/assets/images/flags/usa.png',
    es: '/assets/images/flags/spain.png',
  };
  
  const languageNames: Record<SupportedLanguage, string> = {
    pt: 'Português',
    en: 'English',
    es: 'Español'
  };
  
  const renderMobileDrawer = (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: 'linear-gradient(to bottom, #FFFFFF, #F5F9F9)',
        overflow: 'hidden'
      }}
    >
      {/* Drawer Header with Logo */}
      <Box
        sx={{
          pt: 4,
          pb: 3,
          px: 3,
          borderBottom: '1px solid rgba(0, 77, 64, 0.08)',
          background: 'linear-gradient(135deg, #FFFFFF 0%, #F5FFFD 100%)',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          position: 'relative'
        }}
      >
        {/* Logo */}
        <Box
          sx={{
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Box
            sx={{
              p: 1.5,
              borderRadius: '50%',
              backgroundColor: 'white',
              boxShadow: '0 4px 12px rgba(0, 77, 64, 0.12)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 1
            }}
          >
            <img
              src="/assets/images/logos/turoad_logo.png"
              alt="TURoad Logo"
              style={{ height: '55px', width: 'auto' }}
            />
          </Box>
        </Box>
        
        {/* Description */}
        <Typography
          variant="body2"
          sx={{
            color: 'var(--text-color)',
            opacity: 0.8,
            textAlign: 'center',
            fontStyle: 'italic',
            fontWeight: 400,
            fontSize: '0.9rem',
            lineHeight: 1.5,
            maxWidth: '85%',
            mb: 1
          }}
        >
          Histórias que ligam pessoas a lugares no Brasil
        </Typography>
      </Box>

      {/* Navigation Items */}
      <List
        sx={{
          flex: 1,
          py: 2,
          px: 1.5,
          overflowY: 'auto'
        }}
      >
        {navItems.map((item, index) => (
          <ListItem
            key={item.path}
            disablePadding
            sx={{
              mb: 0.5,
              overflow: 'hidden',
              borderRadius: '8px',
              transition: 'all 0.3s ease'
            }}
          >
            <Box
              onClick={() => {
                navigate(item.path);
                setMobileOpen(false);
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                py: 1.5,
                px: 2.5,
                cursor: 'pointer',
                color: isActive(item.path) ? 'var(--primary-color)' : 'var(--text-color)',
                backgroundColor: isActive(item.path) ? 'rgba(0, 77, 64, 0.08)' : 'transparent',
                borderLeft: isActive(item.path) ? '3px solid var(--primary-color)' : '3px solid transparent',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: isActive(item.path) ? 'rgba(0, 77, 64, 0.12)' : 'rgba(0, 77, 64, 0.04)',
                  transform: 'translateX(4px)'
                }
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  fontWeight: isActive(item.path) ? 600 : 500,
                  fontSize: '1rem',
                  letterSpacing: '0.3px'
                }}
              >
                {item.label}
              </Typography>
            </Box>
          </ListItem>
        ))}
      </List>

      {/* Footer with Language Selector */}
      <Box
        sx={{
          mt: 'auto',
          p: 3,
          borderTop: '1px solid rgba(0, 77, 64, 0.08)',
          backgroundColor: 'rgba(0, 77, 64, 0.02)'
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            mb: 2,
            color: 'var(--text-color)',
            opacity: 0.8,
            fontWeight: 500,
            textAlign: 'center',
            fontSize: '0.85rem',
            letterSpacing: '0.5px',
            textTransform: 'uppercase'
          }}
        >
          {t('language')}
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Box
            onClick={handleLanguageMenuOpen}
            sx={{
              display: 'flex',
              alignItems: 'center',
              borderRadius: '24px',
              padding: '10px 16px',
              border: '1px solid rgba(0, 77, 64, 0.15)',
              backgroundColor: 'white',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: 'rgba(0, 77, 64, 0.04)',
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.07)'
              }
            }}
          >
            <LanguageFlag
              src={flagImages[language]}
              alt={language.toUpperCase()}
              className="active"
            />
            <Box sx={{ ml: 1.5, display: 'flex', alignItems: 'center' }}>
              <Typography
                variant="body2"
                sx={{
                  mr: 0.5,
                  fontWeight: 600,
                  color: 'var(--primary-color)',
                  fontSize: '0.95rem'
                }}
              >
                {languageNames[language as SupportedLanguage]}
              </Typography>
              <KeyboardArrowDownIcon
                fontSize="small"
                sx={{
                  color: 'var(--primary-color)',
                  opacity: 0.8,
                  transition: 'transform 0.3s ease',
                  transform: Boolean(languageMenuAnchor) ? 'rotate(180deg)' : 'rotate(0deg)'
                }}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
  
  return (
    <motion.div
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <StyledAppBar position="sticky">
        <Container maxWidth="xl">
          <Toolbar disableGutters>
            {/* Logo e texto para mobile */}
            {isMobile ? (
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', height: '80px', justifyContent: 'space-between', pr: 1 }}>
                <RouterLink to="/" style={{ display: 'flex', alignItems: 'center' }}>
                  <Logo src="/assets/images/logos/turoad_logo.png" alt="TURoad Logo" />
                </RouterLink>

                <Box sx={{ flex: 1, mx: 2, display: { xs: 'flex', sm: 'flex', md: 'none' }, justifyContent: 'center' }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontStyle: 'italic',
                      color: 'var(--text-color)',
                      opacity: 0.75,
                      textAlign: 'center',
                      fontSize: '0.85rem',
                      lineHeight: 1.3
                    }}
                  >
                    Histórias que ligam pessoas a lugares no Brasil
                  </Typography>
                </Box>
                
                <IconButton
                  color="inherit"
                  aria-label="open drawer"
                  onClick={handleDrawerToggle}
                  sx={{ color: 'text.primary' }}
                >
                  <MenuIcon />
                </IconButton>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexGrow: { xs: 1, md: 0 }, height: '80px' }}>
                <RouterLink to="/" style={{ display: 'flex', alignItems: 'center' }}>
                  <Logo src="/assets/images/logos/turoad_logo.png" alt="TURoad Logo" />
                </RouterLink>
              </Box>
            )}
            
            {/* Mobile menu button - movido para dentro da condição acima */}
            
            {/* Desktop navigation */}
            {!isMobile && (
              <MenuContainer>
                {navItems.map((item) => (
                  <NavButton
                    key={item.path}
                    onClick={() => navigate(item.path)}
                    className={isActive(item.path) ? 'active' : ''}
                  >
                    <span>{item.label}</span>
                  </NavButton>
                ))}
              </MenuContainer>
            )}
            
            {/* Language selector (desktop) */}
            {!isMobile && (
              <LanguageSelector onClick={handleLanguageMenuOpen}>
                <LanguageFlag 
                  src={flagImages[language]} 
                  alt={language.toUpperCase()} 
                  className="active" 
                />
                <Box sx={{ ml: 1.5, display: 'flex', alignItems: 'center' }}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      mr: 0.5, 
                      fontWeight: 500, 
                      color: 'var(--primary-color)', 
                      fontSize: '0.9rem' 
                    }}
                  >
                    {languageNames[language as SupportedLanguage]}
                  </Typography>
                  <KeyboardArrowDownIcon 
                    fontSize="small" 
                    sx={{ 
                      color: 'var(--primary-color)', 
                      opacity: 0.7,
                      transition: 'transform 0.2s ease',
                      transform: Boolean(languageMenuAnchor) ? 'rotate(180deg)' : 'rotate(0deg)'
                    }} 
                  />
                </Box>
              </LanguageSelector>
            )}
            
            {/* Language menu */}
            <Menu
              anchorEl={languageMenuAnchor}
              open={Boolean(languageMenuAnchor)}
              onClose={handleLanguageMenuClose}
              onClick={(e) => e.stopPropagation()}
              elevation={2}
              PaperProps={{
                sx: {
                  mt: 1.5,
                  boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                  borderRadius: '8px',
                  minWidth: '150px',
                  zIndex: 1500
                }
              }}
              style={{ zIndex: 1400 }}
              slotProps={{
                paper: { style: { zIndex: 1300 } }
              }}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              {(Object.keys(flagImages) as SupportedLanguage[]).map((lang) => (
                <MenuItem 
                  key={lang} 
                  onClick={(e) => {
                    e.stopPropagation();
                    changeLanguage(lang);
                  }}
                  selected={lang === language}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    px: 2,
                    py: 1.5,
                    '&.Mui-selected': {
                      backgroundColor: 'rgba(0, 96, 100, 0.08)',
                    },
                    '&:hover': {
                      backgroundColor: 'rgba(0, 96, 100, 0.04)',
                    }
                  }}
                >
                  <LanguageFlag 
                    src={flagImages[lang]} 
                    alt={lang.toUpperCase()} 
                    style={{ marginRight: '12px' }} 
                  />
                  <Typography variant="body2">
                    {languageNames[lang]}
                  </Typography>
                </MenuItem>
              ))}
            </Menu>
          </Toolbar>
        </Container>
      </StyledAppBar>
      
      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        anchor="left"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ 
          keepMounted: true,
          disableScrollLock: true,
          disablePortal: false,
          container: document.body
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box', 
            width: 280,
            overflowY: 'auto'
          },
          '& .MuiBackdrop-root': { 
            backgroundColor: 'rgba(0, 0, 0, 0.6)' 
          }
        }}
        PaperProps={{
          sx: {
            width: 280,
            border: 'none',
            boxShadow: '0 4px 25px rgba(0, 0, 0, 0.25)'
          }
        }}
      >
        {renderMobileDrawer}
      </Drawer>
    </motion.div>
  );
};

export default Header;
